import React from 'react';
import {
    Box, Button, Checkbox, FormControl,
    InputLabel, ListItemText, ListSubheader, MenuItem, Select, SelectProps,
} from '@material-ui/core';
import { compose, head, keys, noop, omitBy, size } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useHistory } from 'react-router-dom';
import { DefaultFormFields } from '../../../../ui-kit/FilterComponent/types';
import { MenuListProps } from '../../../../constants';
import { setUrlParams } from '../../../../utils/helpers/urlParams';
import { MoreFiltersOrder } from '../../../../generated/graphql';

interface MoreFiltersProps<T> {
    formData: T;
    setFormData: React.Dispatch<React.SetStateAction<T>>;
}

const getMoreFiltersText = (selectedOptions: string[], t: TFunction) => {
    const selectedCount = size(selectedOptions);
    if (selectedCount === 1) {
        return t(head(selectedOptions) || '');
    }
    if (selectedCount > 1) {
        return `${head(selectedOptions)} ${selectedCount - 1}`;
    }
    return t('More filters');
};

const MoreFilters = <T extends DefaultFormFields,>({ formData, setFormData }: MoreFiltersProps<T>) => {
    const history = useHistory();

    const handleChange: SelectProps['onChange'] = event => {
        if (event.target.value) {
            const values = event.target.value as string[];
            if (values[0]) {
                const key = values[0] as keyof MoreFiltersOrder;
                if (key === 'open') {
                    const urlParams = !formData.moreFilters[key]
                        ? setUrlParams(history.location.search, { open: '1' })
                        : '';
                    history.push({ search: urlParams.toString() });
                }
                setFormData({ ...formData, moreFilters: { ...formData.moreFilters, [key]: !formData.moreFilters[key] } });
            }
        }
    };
    const { t } = useTranslation();

    const selectedOptions = compose(keys, omitBy(option => option === false))(formData.moreFilters);

    const resetMoreFilters = () => setFormData({
        ...formData,
        moreFilters: {
            multiple: false,
            single: false,
            onPlace: false,
            toGo: false,
            open: false,
            closed: false,
            canceled: false,
        }
    });

    return (
        <Box minWidth={144} maxWidth={320} width='100%'>
            <FormControl fullWidth variant="outlined" size='small'>
                <InputLabel>{getMoreFiltersText(selectedOptions, t)}</InputLabel>
                <Select
                    variant='outlined'
                    label={getMoreFiltersText(selectedOptions, t)}
                    renderValue={() => noop}
                    multiple
                    name='moreFilters'
                    value={[]}
                    onChange={handleChange}
                    MenuProps={{
                        ...MenuListProps,
                        PaperProps: {
                            style: { maxHeight: 'unset' },
                        }
                    }}
                >
                    <MenuItem value=''>
                        <Button fullWidth onClick={resetMoreFilters}>{t('Reset')}</Button>
                    </MenuItem>
                    <ListSubheader>{t('Amount of bills in order')}</ListSubheader>
                    <MenuItem value='multiple'>
                        <Checkbox color='primary' name='multiple' checked={!!formData.moreFilters.multiple} />
                        <ListItemText primary={t('Multiple')} />
                    </MenuItem>
                    <MenuItem value='single'>
                        <Checkbox color='primary' name='single' checked={!!formData.moreFilters.single} />
                        <ListItemText primary={t('Single')} />
                    </MenuItem>
                    <ListSubheader>{t('Order type')}</ListSubheader>
                    <MenuItem value='onPlace'>
                        <Checkbox color='primary' name='onPlace' checked={!!formData.moreFilters.onPlace} />
                        <ListItemText primary={t('On Place')} />
                    </MenuItem>
                    <MenuItem value='toGo'>
                        <Checkbox color='primary' name='toGo' checked={!!formData.moreFilters.toGo} />
                        <ListItemText primary={t('To go')} />
                    </MenuItem>
                    <ListSubheader>{t('Order state')}</ListSubheader>
                    <MenuItem value='open'>
                        <Checkbox color='primary' name='open' checked={!!formData.moreFilters.open} />
                        <ListItemText primary={t('Open' )} />
                    </MenuItem>
                    <MenuItem value='closed'>
                        <Checkbox color='primary' name='closed' checked={!!formData.moreFilters.closed} />
                        <ListItemText primary={t('Closed' )} />
                    </MenuItem>
                    <MenuItem value='canceled'>
                        <Checkbox color='primary' name='canceled' checked={!!formData.moreFilters.canceled} />
                        <ListItemText primary={t('Canceled' )} />
                    </MenuItem>
                </Select>
            </FormControl>
        </Box>
    );
};

export default MoreFilters;
